/**
 * Article Detail Page - REDESIGNED
 * Layout: Title → Type/Date/Author → Cover Photo → Body Text → Gallery
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import Gallery from '../components/Gallery';
import Navbar from '../components/Navbar';
import { navigateBack, cameFrom } from '../utils/navigationHistory';
import '../styles/colors.css';

const ArticleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [creator, setCreator] = useState(null);
  const [collaborators, setCollaborators] = useState([]);

  useEffect(() => {
    fetchArticle();
    incrementViewCount();
  }, [id]);

  const fetchArticle = async () => {
    try {
      const response = await apiClient.get(`/articles/${id}`);
      if (response.data.success) {
        setArticle(response.data.article);
        
        try {
          const creatorResponse = await apiClient.get(`/users/${response.data.article.created_by}`);
          if (creatorResponse.data.success) {
            setCreator(creatorResponse.data.user);
          }
        } catch (err) {
          console.error('Failed to load creator:', err);
        }

        try {
          const collabResponse = await apiClient.get(`/articles/${id}/collaborators`);
          if (collabResponse.data.success) {
            setCollaborators(collabResponse.data.collaborators);
          }
        } catch (err) {
          console.error('Failed to load collaborators:', err);
        }
      }
    } catch (err) {
      setError('Article not found');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const incrementViewCount = async () => {
    try {
      await apiClient.post(`/articles/${id}/increment-view`);
    } catch (err) {
      console.error('Failed to increment view count:', err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    setDeleting(true);
    try {
      const response = await apiClient.delete(`/articles/${id}`);
      if (response.data.success) {
        alert('Article deleted successfully');
        navigate('/news?tab=articles');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete article');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-light)' }}>
          Loading article...
        </div>
      </>
    );
  }

  if (error || !article) {
    return (
      <>
        <Navbar />
        <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '20px' }}>
          <p style={{ color: 'var(--accent-danger)', fontSize: '18px' }}>{error || 'Article not found'}</p>
          <button onClick={() => navigate('/news?tab=articles')} className="btn btn-secondary">Back to Articles</button>
        </div>
      </>
    );
  }

  const isCreator = user && user.id === article.created_by;

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .article-title {
            font-size: 32px !important;
          }

          .article-cover-photo {
            max-height: 400px !important;
          }

          .article-body-card {
            padding: 24px !important;
          }

          .article-meta-line {
            display: flex !important;
            flex-direction: column !important;
            gap: 8px !important;
            align-items: flex-start !important;
          }
        }
      `}</style>
      <Navbar />
      <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', paddingTop: '140px', paddingBottom: '60px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 20px' }}>

          <h1 className="article-title" style={{ color: 'var(--text-light)', fontSize: '48px', fontWeight: '700', marginBottom: '20px', lineHeight: '1.2' }}>
            {article.description}
          </h1>

          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <span className="badge badge-primary" style={{ fontSize: '14px', padding: '8px 16px' }}>Article</span>
            {article.article_type && article.article_type.length > 0 && article.article_type.map((type, i) => (
              <span key={i} style={{ background: 'var(--smoke-gray)', color: 'var(--text-light)', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '700', textTransform: 'capitalize' }}>
                {type.replace(/_/g, ' ')}
              </span>
            ))}
          </div>

          <div className="article-meta-line" style={{ color: 'var(--text-light)', fontSize: '15px', marginBottom: '32px', opacity: 0.8 }}>
            <div>
              {creator && (
                <span>
                  By{' '}
                  <a href={`/users/${creator.id}`} onClick={(e) => { e.preventDefault(); navigate(`/users/${creator.id}`); }} 
                     style={{ color: 'var(--accent-primary)', textDecoration: 'underline', cursor: 'pointer' }}>
                    {creator.display_name}
                  </a>
                </span>
              )}
              {collaborators.length > 0 && (
                <span>
                  {' · Collaborators: '}
                  {collaborators.filter(c => c.collaboration_status === 'accepted').map((c, i, arr) => (
                    <span key={c.id}>
                      {c.collaborator_id ? (
                        <a href={`/users/${c.collaborator_id}`} onClick={(e) => { e.preventDefault(); navigate(`/users/${c.collaborator_id}`); }} 
                           style={{ color: 'var(--accent-primary)', textDecoration: 'underline', cursor: 'pointer' }}>
                          {c.collaborator_name}
                        </a>
                      ) : c.collaborator_name}
                      {i < arr.length - 1 && ', '}
                    </span>
                  ))}
                </span>
              )}
            </div>
            <div>
              {formatDate(article.created_at)}
              {article.updated_at !== article.created_at && (
                <span> · Updated {formatDate(article.updated_at)}</span>
              )}
            </div>
          </div>

          {article.cover_photo_url && (
            <div style={{ marginBottom: '40px', textAlign: 'center' }}>
              <img src={article.cover_photo_url} alt={article.description} className="article-cover-photo"
                   style={{ maxWidth: '100%', maxHeight: '500px', borderRadius: '12px', boxShadow: 'var(--shadow-card)', objectFit: 'cover' }}
                   onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
          )}

          <div className="article-body-card" style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '40px', marginBottom: '32px', boxShadow: 'var(--shadow-card)' }}>
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: '17px', color: 'var(--text-dark)' }}>
              {article.body}
            </div>
          </div>

          {article.image_gallery_urls && article.image_gallery_urls.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <h2 style={{ color: 'var(--text-light)', fontSize: '24px', fontWeight: '700', marginBottom: '20px' }}>Gallery</h2>
              <Gallery images={article.image_gallery_urls} />
            </div>
          )}

          {isCreator && (
            <div style={{ background: 'rgba(0, 217, 255, 0.1)', border: '2px solid var(--accent-primary)', borderRadius: '12px', padding: '24px', marginTop: '32px' }}>
              <p style={{ color: 'var(--text-light)', marginBottom: '16px', fontSize: '16px' }}><strong>You created this article</strong></p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => navigate(`/articles/${id}/edit`)} className="btn btn-primary">Edit Article</button>
                <button onClick={handleDelete} disabled={deleting} className="btn btn-danger">{deleting ? 'Deleting...' : 'Delete Article'}</button>
              </div>
            </div>
          )}

          <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)', fontSize: '13px', color: 'var(--text-muted)' }}>
            {article.view_count > 0 && <span>👁 {article.view_count} views</span>}
          </div>
        </div>
      </div>
    </>
  );
};

export default ArticleDetailPage;
